import deepmerge from 'deepmerge'
import { isEqual } from 'lodash'
import {
  CASES_PAGE_DEFAULT_SORT,
  LEGACY_CASES_PAGE_DEFAULT_SORT,
} from './casesPageDefaultSort'
import {
  LOCAL_PERSISTED_MODULES,
  LOCAL_STORAGE_KEY,
  SESSION_PERSISTED_MODULES,
  SESSION_STORAGE_KEY,
} from './persistedModules'

/**
 * State persistence for the Pinia stores, replacing vuex-persist.
 *
 * It reproduces vuex-persist's semantics deliberately, because the storage
 * format is shared with the live Bridge app:
 *   - RESTORE merges the saved blob into state, deeply, with arrays replaced
 *     rather than concatenated (its default `replaceArrays`), and is NOT
 *     filtered by the module lists — those are a save-time filter only. That
 *     asymmetry is real, and is what made the legacy loggedInUser entry
 *     re-hydrate for so long.
 *   - SAVE writes only the listed modules, dropping every other key, on every
 *     change.
 * `deepmerge` is used for the merge rather than a hand-rolled one (and rather
 * than lodash's `merge`, which merges arrays element-wise: merging [9] into
 * [1,2,3] would yield [9,2,3]) so restore stays byte-identical to what ships.
 *
 * This layer replaced vuex-persist before any module had converted, because the
 * two engines shared the `vuex` localStorage blob and a Vuex-only plugin would
 * have rewritten that blob from Vuex state alone, deleting the entries belonging
 * to migrated modules. It carried both engines through the migration; P4a
 * removed the Vuex half along with Vuex itself.
 */

/** A `Storage`-shaped object. Narrowed so tests can pass a plain fake. */
export interface IKeyValueStorage {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
}

interface IPersistenceTarget {
  key: string
  storage: IKeyValueStorage
  modules: readonly string[]
}

/** Merge a restored blob into current state, with arrays replaced. */
export function mergeRestoredState<T extends object>(
  currentState: T,
  savedState: object | undefined
): T {
  return deepmerge(currentState, savedState ?? {}, {
    arrayMerge: (_destination, source) => source,
  }) as T
}

/** Parse a stored blob, tolerating an absent or corrupt value. */
export function readPersistedBlob(
  storage: IKeyValueStorage,
  key: string
): Record<string, any> {
  try {
    const raw = storage.getItem(key)
    if (!raw) {
      return {}
    }
    const parsed = JSON.parse(raw)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch (e) {
    // A corrupt blob must not stop the app from booting; treat it as empty and
    // let the next save overwrite it.
    return {}
  }
}

/**
 * Compose the blob to write for one storage target.
 *
 * Each listed module is written from its live Pinia store if one has been
 * instantiated, and otherwise CARRIED FORWARD from what was last saved. Pinia
 * creates stores lazily, so at any moment most of them do not exist; without the
 * carry-forward the first save of the session would drop the saved state of
 * every module the user had not yet visited.
 *
 * Branch order is load-bearing: a live store must win over the stored copy,
 * which goes stale the moment that store is written to.
 *
 * Carry-forward is deliberately not conditioned on the name being a registered
 * store. Six names on the local list have no store module and never have had one
 * (see persistedModules.ts); under Vuex they survived a save because restore was
 * unfiltered — the whole blob was merged into Vuex ROOT state, so every stale key
 * came back as an orphan and was written straight out again. Carrying forward
 * anything already in the blob reproduces that exactly, which is what keeps the
 * bytes identical to the Bridge app's.
 *
 * Anything not in `moduleNames` is dropped, matching vuex-persist.
 */
export function buildPersistedBlob({
  moduleNames,
  previousBlob,
  piniaStates,
}: {
  moduleNames: readonly string[]
  previousBlob: Record<string, any>
  /** State of the Pinia stores that have actually been instantiated. */
  piniaStates: Record<string, any>
}): Record<string, any> {
  const blob: Record<string, any> = {}

  for (const name of moduleNames) {
    if (name in piniaStates) {
      blob[name] = piniaStates[name]
      continue
    }
    if (previousBlob[name] !== undefined) {
      blob[name] = previousBlob[name]
    }
  }

  return blob
}

/**
 * One-time migration: loggedInUser used to be persisted in the localStorage
 * blob. Restore merges the whole blob back into state regardless of the module
 * filter, so a stale loggedInUser left there would be re-hydrated once (with no
 * token), reintroducing the very desync that moving it to sessionStorage fixed.
 * Strip it before anything restores. Safe to run on every load; it is a no-op
 * once the key is gone.
 */
export function dropLegacyLoggedInUserFromLocalStorage(
  storage: IKeyValueStorage
): void {
  try {
    const raw = storage.getItem(LOCAL_STORAGE_KEY)
    if (!raw) {
      return
    }
    const parsed = JSON.parse(raw)
    if (
      parsed &&
      Object.prototype.hasOwnProperty.call(parsed, 'loggedInUser')
    ) {
      delete parsed.loggedInUser
      storage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(parsed))
    }
  } catch (e) {
    // Corrupt/absent blob — nothing to migrate.
  }
}

/** The 'Locations Affected' sort, since removed from the Cases and Service Requests menus. */
const RETIRED_LOCATIONS_AFFECTED_SORT_KEY = 'numLocationsAffected'

/**
 * One-time migration: the Cases list's default sort was Case ID with no
 * direction -- OLDEST first -- and is now Opened Date, newest first
 * (CASES_PAGE_DEFAULT_SORT). casesPage is persisted, and restore merges a saved
 * sort over the default, so a saved copy of the OLD default would keep its
 * Case ID: the new default would never reach anyone who had opened the page.
 *
 * Only that exact object is replaced. It cannot be a user's choice -- no menu
 * entry has that shape -- so anyone who picked a sort keeps it. Safe to run on
 * every load; a no-op once the old default is gone.
 *
 * Also replaced: a saved 'Locations Affected' sort (`numLocationsAffected`),
 * taken out of the menu because the list has no such column. Kept, it would go
 * on sorting by a value nobody can see, under a label the menu no longer offers.
 */
export function upgradeLegacyCasesPageDefaultSort(
  storage: IKeyValueStorage
): void {
  try {
    const raw = storage.getItem(LOCAL_STORAGE_KEY)
    if (!raw) {
      return
    }
    const parsed = JSON.parse(raw)
    const savedSort = parsed?.casesPage?.sortAllBy
    if (
      isEqual(savedSort, LEGACY_CASES_PAGE_DEFAULT_SORT) ||
      savedSort?.sortKey === RETIRED_LOCATIONS_AFFECTED_SORT_KEY
    ) {
      parsed.casesPage.sortAllBy = { ...CASES_PAGE_DEFAULT_SORT }
      storage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(parsed))
    }
  } catch (e) {
    // Corrupt/absent blob — nothing to migrate.
  }
}

/**
 * One-time migration, the Service Requests twin of the Locations Affected half
 * of upgradeLegacyCasesPageDefaultSort: that sort left the menu because the
 * list has no such column.
 *
 * The saved sort is DROPPED rather than overwritten. Restore merges the blob
 * into the store's initial state, so with no saved sort the page opens on the
 * store's own default (stores/serviceRequestsPage.ts) -- which this function
 * then never has to repeat or keep in step with. Every other saved sort is
 * left alone. Safe to run on every load.
 */
export function dropRetiredServiceRequestsPageSort(
  storage: IKeyValueStorage
): void {
  try {
    const raw = storage.getItem(LOCAL_STORAGE_KEY)
    if (!raw) {
      return
    }
    const parsed = JSON.parse(raw)
    const page = parsed?.serviceRequestsPage
    if (page?.sortAllBy?.sortKey === RETIRED_LOCATIONS_AFFECTED_SORT_KEY) {
      delete page.sortAllBy
      storage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(parsed))
    }
  } catch (e) {
    // Corrupt/absent blob — nothing to migrate.
  }
}

/**
 * Build the persistence layer.
 *
 * Reads and writes window storage, so this is only invoked on the client.
 */
export function createStorePersistence(storages: {
  localStorage: IKeyValueStorage
  sessionStorage: IKeyValueStorage
}) {
  dropLegacyLoggedInUserFromLocalStorage(storages.localStorage)
  upgradeLegacyCasesPageDefaultSort(storages.localStorage)
  dropRetiredServiceRequestsPageSort(storages.localStorage)

  // Session first, then local — the order vuex-persist's two plugin instances
  // ran in, which decides the winner if a key somehow appears in both.
  const targets: IPersistenceTarget[] = [
    {
      key: SESSION_STORAGE_KEY,
      storage: storages.sessionStorage,
      modules: SESSION_PERSISTED_MODULES,
    },
    {
      key: LOCAL_STORAGE_KEY,
      storage: storages.localStorage,
      modules: LOCAL_PERSISTED_MODULES,
    },
  ]

  const piniaStores = new Map<string, { $state: Record<string, any> }>()

  function save(): void {
    const piniaStates: Record<string, any> = {}
    for (const [id, store] of piniaStores) {
      piniaStates[id] = store.$state
    }

    for (const target of targets) {
      const blob = buildPersistedBlob({
        moduleNames: target.modules,
        // Re-read on every save rather than caching: this is the source of the
        // carry-forward entries, and it has to reflect what is on disk now, not
        // what was there at boot.
        previousBlob: readPersistedBlob(target.storage, target.key),
        piniaStates,
      })
      target.storage.setItem(target.key, JSON.stringify(blob))
    }
  }

  return {
    /**
     * Pinia plugin: hydrate this store from the blobs, then persist on change.
     *
     * Hydration is offered to every store, whether or not it is on a persisted
     * list — the same restore/save asymmetry vuex-persist had, which matters for
     * a module that has stale saved state but is not on a list. Only listed
     * modules are registered for saving.
     */
    piniaPlugin(context: {
      store: {
        $id: string
        $state: Record<string, any>
        $patch: (s: any) => void
        $subscribe: (cb: () => void) => void
      }
    }): void {
      const { store } = context

      const saved = targets.reduce<Record<string, any>>(
        (merged, target) =>
          mergeRestoredState(
            merged,
            readPersistedBlob(target.storage, target.key)[store.$id]
          ),
        {}
      )
      if (Object.keys(saved).length > 0) {
        store.$patch(saved)
      }

      const isPersisted = targets.some((target) =>
        target.modules.includes(store.$id)
      )
      if (!isPersisted) {
        return
      }

      piniaStores.set(store.$id, store)
      store.$subscribe(() => save())
      // The store may have hydrated above, or arrived with defaults that differ
      // from what was last saved (a new field, say). Write once now so the blob
      // reflects the live store rather than waiting for the first change.
      save()
    },
  }
}
